;(function () {
	window.__lCorpRevealActive = true

	// Pointer travel (px) that turns a press into a drag. Below this it stays a
	// candidate click so the underlying card link/button keeps its native click.
	const DRAG_THRESHOLD = 6

	let corporateRevealObserver = null

	if (!customElements.get('l-corporate-carousel')) {
		class LTACorporateCarousel extends HTMLElement {
			connectedCallback() {
				this.viewport = this.querySelector('[data-corporate-carousel-viewport]')
				this.fill = this.querySelector('[data-corporate-carousel-fill]')
				this.prevBtn = this.querySelector('[data-corporate-carousel-prev]')
				this.nextBtn = this.querySelector('[data-corporate-carousel-next]')
				if (!this.viewport) return

				this._onScroll = this._update.bind(this)
				this._onPrev = this._scrollByPage.bind(this, -1)
				this._onNext = this._scrollByPage.bind(this, 1)
				this._onPointerDown = this._handlePointerDown.bind(this)
				this._onPointerMove = this._handlePointerMove.bind(this)
				this._onPointerUp = this._handlePointerUp.bind(this)
				this._onClick = this._handleClick.bind(this)
				this._scrollTween = null
				this._dragState = {
					pending: false,
					active: false,
					moved: false,
					captured: false,
					pointerId: null,
					startX: 0,
					startScroll: 0,
					lastScroll: 0,
					lastTime: 0,
					velocity: 0,
				}
				this._preventNextClick = false

				this.viewport.addEventListener('scroll', this._onScroll, { passive: true })
				this.viewport.addEventListener('pointerdown', this._onPointerDown)
				this.viewport.addEventListener('pointermove', this._onPointerMove)
				this.viewport.addEventListener('pointerup', this._onPointerUp)
				this.viewport.addEventListener('pointercancel', this._onPointerUp)
				this.viewport.addEventListener('click', this._onClick, true)
				window.addEventListener('resize', this._onScroll)
				if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev)
				if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext)

				requestAnimationFrame(this._onScroll)
			}

			disconnectedCallback() {
				if (this.viewport) this.viewport.removeEventListener('scroll', this._onScroll)
				if (this.viewport) this.viewport.removeEventListener('pointerdown', this._onPointerDown)
				if (this.viewport) this.viewport.removeEventListener('pointermove', this._onPointerMove)
				if (this.viewport) this.viewport.removeEventListener('pointerup', this._onPointerUp)
				if (this.viewport) this.viewport.removeEventListener('pointercancel', this._onPointerUp)
				if (this.viewport) this.viewport.removeEventListener('click', this._onClick, true)
				window.removeEventListener('resize', this._onScroll)
				if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev)
				if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext)
			}

			_handlePointerDown(event) {
				if (!this.viewport || event.button !== 0) return
				if (this.viewport.scrollWidth <= this.viewport.clientWidth) return

				// Record the press but do NOT engage the drag or capture the pointer
				// yet. Capturing on pointerdown retargets the resulting mouseup/click to
				// the viewport, so taps on a card's link or add-to-cart button never
				// reach them. We only commit to a drag once the pointer travels past
				// DRAG_THRESHOLD (see _handlePointerMove → _engageDrag).
				this._killScrollTween()
				this._preventNextClick = false
				this._dragState.pending = true
				this._dragState.active = false
				this._dragState.moved = false
				this._dragState.captured = false
				this._dragState.pointerId = event.pointerId
				this._dragState.startX = event.clientX
				this._dragState.startScroll = this.viewport.scrollLeft
				this._dragState.lastScroll = this.viewport.scrollLeft
				this._dragState.lastTime = performance.now()
				this._dragState.velocity = 0
				this.viewport.classList.remove('is-settling')
			}

			_handlePointerMove(event) {
				if (!this.viewport) return
				if (!this._dragState.pending && !this._dragState.active) return

				// Ignore events from a different pointer (e.g. a second touch point).
				if (this._dragState.pointerId !== null && event.pointerId !== this._dragState.pointerId) return

				// While pending we haven't captured the pointer yet, so a release outside
				// the viewport never reaches pointerup and leaves pending set. If no
				// button is pressed the press is already over — drop it so a later
				// hover-move can't start a phantom drag.
				if (!this._dragState.active && event.buttons === 0) {
					this._dragState.pending = false
					return
				}

				const delta = this._dragState.startX - event.clientX

				// Still a candidate click until the pointer moves far enough — leave the
				// underlying link/button untouched so its native click survives.
				if (!this._dragState.active) {
					if (Math.abs(delta) <= DRAG_THRESHOLD) return
					this._engageDrag(event)
				}

				const now = performance.now()
				const nextScroll = this._clampScroll(this._dragState.startScroll + delta)
				const dt = Math.max(1, now - this._dragState.lastTime)

				event.preventDefault()
				this._dragState.velocity = (nextScroll - this._dragState.lastScroll) / dt
				this._dragState.lastScroll = nextScroll
				this._dragState.lastTime = now
				this.viewport.scrollLeft = nextScroll
				this._update()
			}

			_engageDrag(event) {
				this._dragState.active = true
				this._dragState.moved = true
				this.viewport.classList.add('is-dragging')

				// Capture now (mid-drag) so scrolling keeps working if the pointer
				// leaves the viewport. The trailing click after a drag is suppressed in
				// _handleClick regardless of where capture retargets it.
				if (this.viewport.setPointerCapture) {
					try {
						this.viewport.setPointerCapture(event.pointerId)
						this._dragState.captured = true
					} catch (error) {
						// Pointer may already be gone; dragging still works without capture.
					}
				}
			}

			_handlePointerUp(event) {
				if (!this.viewport) return
				if (!this._dragState.pending && !this._dragState.active) return

				const moved = this._dragState.moved
				this._preventNextClick = moved
				this._dragState.pending = false
				this._dragState.active = false
				this.viewport.classList.remove('is-dragging')

				if (moved) this._settleAfterDrag()

				if (this._dragState.captured && this.viewport.releasePointerCapture) {
					try {
						this.viewport.releasePointerCapture(event.pointerId)
					} catch (error) {
						// The pointer may already be released by the browser.
					}
				}
				this._dragState.captured = false
			}

			_handleClick(event) {
				if (!this._preventNextClick) return

				event.preventDefault()
				event.stopImmediatePropagation()
				this._preventNextClick = false
			}

			_scrollByPage(dir) {
				if (!this.viewport) return

				const step = this._getCardStep()
				const target = this._getSnapTarget(this.viewport.scrollLeft + dir * step)

				this._animateToScroll(target, 0.58)
			}

			_getMaxScroll() {
				return this.viewport ? Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth) : 0
			}

			_clampScroll(value) {
				return Math.min(this._getMaxScroll(), Math.max(0, value))
			}

			_getCardStep() {
				const firstCard = this.viewport.querySelector('.l-corp-product-card')
				const track = this.viewport.querySelector('.l-corp-carousel__track') || this.viewport
				const styles = window.getComputedStyle(track)
				const gap = parseFloat(styles.columnGap || styles.gap) || 0

				return firstCard ? firstCard.getBoundingClientRect().width + gap : this.viewport.clientWidth
			}

			_getSnapTarget(target) {
				const cards = Array.from(this.viewport.querySelectorAll('.l-corp-product-card'))
				if (!cards.length) return this._clampScroll(target)

				const viewportRect = this.viewport.getBoundingClientRect()
				const styles = window.getComputedStyle(this.viewport)
				const paddingStart = parseFloat(styles.paddingLeft) || 0
				const currentScroll = this.viewport.scrollLeft
				let closest = this._clampScroll(target)
				let closestDistance = Infinity

				cards.forEach((card) => {
					const cardRect = card.getBoundingClientRect()
					const snap = this._clampScroll(cardRect.left - viewportRect.left + currentScroll - paddingStart)
					const distance = Math.abs(snap - target)

					if (distance < closestDistance) {
						closest = snap
						closestDistance = distance
					}
				})

				return closest
			}

			_settleAfterDrag() {
				if (!this.viewport) return

				const projected = this.viewport.scrollLeft + this._dragState.velocity * 260
				const target = this._getSnapTarget(projected)
				const distance = Math.abs(target - this.viewport.scrollLeft)
				const duration = Math.min(0.72, Math.max(0.36, distance / 900))

				this._animateToScroll(target, duration)
			}

			_killScrollTween() {
				if (this._scrollTween && this._scrollTween.kill) {
					this._scrollTween.kill()
				}

				this._scrollTween = null
				if (this.viewport) this.viewport.classList.remove('is-settling')
			}

			_animateToScroll(target, duration) {
				const viewport = this.viewport
				if (!viewport) return

				this._killScrollTween()
				viewport.classList.add('is-settling')

				if (window.gsap) {
					this._scrollTween = window.gsap.to(viewport, {
						scrollLeft: this._clampScroll(target),
						duration: duration,
						ease: 'power3.out',
						overwrite: true,
						onUpdate: () => this._update(),
						onComplete: () => {
							viewport.classList.remove('is-settling')
							this._scrollTween = null
							this._update()
						},
					})

					return
				}

				viewport.scrollTo({ left: this._clampScroll(target), behavior: 'smooth' })
				window.setTimeout(() => {
					viewport.classList.remove('is-settling')
					this._update()
				}, duration * 1000)
			}

			_setDisabled(btn, isDisabled) {
				if (!btn) return
				btn.classList.toggle('is-disabled', isDisabled)
				btn.disabled = isDisabled
				btn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false')
			}

			_update() {
				const viewport = this.viewport
				if (!viewport) return

				const max = viewport.scrollWidth - viewport.clientWidth
				const progress = max > 0 ? Math.min(1, (viewport.scrollLeft + viewport.clientWidth) / viewport.scrollWidth) : 1

				if (this.fill) this.fill.style.setProperty('--l-corp-carousel-progress', progress)
				this._setDisabled(this.prevBtn, viewport.scrollLeft <= 1)
				this._setDisabled(this.nextBtn, viewport.scrollLeft >= max - 1)
			}
		}

		customElements.define('l-corporate-carousel', LTACorporateCarousel)
	}

	/*****************************************
	 * [COMPONENT]: CORPORATE CARD ADD TO CART
	 ******************************************/
	if (!customElements.get('l-corp-atc')) {
		class LTACorpAtc extends HTMLElement {
			constructor() {
				super()

				this._onAddClick = this._onAddClick.bind(this)
				this._stopDrag = this._stopDrag.bind(this)
			}

			connectedCallback() {
				this.addBtn = this.querySelector('[data-atc-submit]')
				this.qtyInput = this.querySelector('[data-qty-input]')

				// The carousel viewport calls setPointerCapture on pointerdown, which
				// otherwise steals the pointer (and the resulting click) from the pill's
				// buttons. Keep pointer events that start on the pill from reaching it.
				this.addEventListener('pointerdown', this._stopDrag)
				if (this.addBtn) this.addBtn.addEventListener('click', this._onAddClick)
			}

			disconnectedCallback() {
				this.removeEventListener('pointerdown', this._stopDrag)
				if (this.addBtn) this.addBtn.removeEventListener('click', this._onAddClick)
			}

			_stopDrag(event) {
				event.stopPropagation()
			}

			_onAddClick() {
				if (!this.addBtn || this.addBtn.disabled || this.addBtn.classList.contains('is-loading')) return
				if (typeof ltaCartService === 'undefined') return

				const variantId = this.dataset.variantId
				if (!variantId) return

				const quantity = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1)
				const item = { id: variantId, quantity: quantity }

				if (this.dataset.sellingPlanId) item.selling_plan = this.dataset.sellingPlanId

				this.addBtn.classList.add('is-loading')

				const config = ltaCartService.createConfigFromItems([item])

				ltaCartService
					.addToCart(config, 'l-corp-atc')
					.catch(function () {})
					.finally(() => {
						this.addBtn.classList.remove('is-loading')
					})
			}
		}

		customElements.define('l-corp-atc', LTACorpAtc)
	}

	function initCorporateReveals(root) {
		const els = getRevealTargets(root, ':not(.is-revealed)')
		const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

		if (!els.length) {
			if (!document.querySelector('[data-l-corp-reveal]:not(.is-revealed)')) {
				document.documentElement.classList.remove('l-corp-reveal-prep')
			}

			return
		}

		if (prefersReducedMotion || !window.gsap || !window.IntersectionObserver) {
			revealCorporateStatic(root)

			return
		}

		if (corporateRevealObserver) corporateRevealObserver.disconnect()

		corporateRevealObserver = new IntersectionObserver(handleCorporateRevealEntries, {
			root: null,
			rootMargin: '0px 0px -16% 0px',
			threshold: 0.01,
		})

		els.forEach(function (el) {
			el.classList.add('is-reveal-bound')
			corporateRevealObserver.observe(el)
		})
	}

	function getRevealScope(root) {
		if (root && root.querySelectorAll) return root

		return document
	}

	function getRevealTargets(root, suffix) {
		const scope = getRevealScope(root)
		const selector = '[data-l-corp-reveal]' + (suffix || '')
		const els = Array.from(scope.querySelectorAll(selector))

		if (scope.matches && scope.matches(selector)) {
			els.unshift(scope)
		}

		return els
	}

	function handleCorporateRevealEntries(entries) {
		entries.forEach(function (entry) {
			if (!entry.isIntersecting) return

			corporateRevealObserver.unobserve(entry.target)
			try {
				playCorporateReveal(entry.target)
			} catch (error) {
				markCorporateRevealComplete(entry.target)
			}
		})
	}

	function playCorporateReveal(el) {
		if (el.classList.contains('is-revealed') || el.classList.contains('is-revealing')) return

		const config = getCorporateRevealConfig(el)
		const parts = getCorporateRevealParts(el)
		const media = getCorporateRevealMedia(el)
		const animatedTargets = [el].concat(parts, media)
		const tl = window.gsap.timeline({
			defaults: {
				ease: 'power3.out',
				overwrite: 'auto',
			},
			onComplete: function () {
				markCorporateRevealComplete(el)
				window.gsap.set(animatedTargets.length ? animatedTargets : el, {
					clearProps: 'opacity,visibility,transform,clipPath,willChange',
				})
			},
		})

		el.classList.add('is-revealing')
		window.gsap.set(animatedTargets.length ? animatedTargets : el, { willChange: 'transform,opacity' })

		tl.fromTo(el, getCorporateRevealFrom(config.shell), getCorporateRevealTo(config.shell), 0)

		if (parts.length) {
			tl.fromTo(parts, getCorporateRevealFrom(config.parts), getCorporateRevealTo(config.parts), 0.08)
		}

		if (media.length) {
			tl.fromTo(media, getCorporateRevealFrom(config.media), getCorporateRevealTo(config.media), config.mediaAt)
		}
	}

	function markCorporateRevealComplete(el) {
		el.classList.remove('is-reveal-bound')
		el.classList.remove('is-revealing')
		el.classList.add('is-revealed')

		if (!document.querySelector('[data-l-corp-reveal]:not(.is-revealed)')) {
			document.documentElement.classList.remove('l-corp-reveal-prep')
		}
	}

	function revealCorporateStatic(root) {
		document.documentElement.classList.remove('l-corp-reveal-prep')
		getRevealTargets(root).forEach(function (el) {
			markCorporateRevealComplete(el)
		})
	}

	function getCorporateRevealFrom(vars) {
		const from = {}

		copyCorporateRevealProp(vars, from, 'clipPath')
		copyCorporateRevealProp(vars, from, 'scale')
		copyCorporateRevealProp(vars, from, 'transformOrigin')
		copyCorporateRevealProp(vars, from, 'x')
		copyCorporateRevealProp(vars, from, 'y')

		if (vars.opacity !== undefined) from.autoAlpha = vars.opacity

		return from
	}

	function getCorporateRevealTo(vars) {
		const to = {}

		copyCorporateRevealProp(vars, to, 'clipPath')
		copyCorporateRevealProp(vars, to, 'duration')
		copyCorporateRevealProp(vars, to, 'ease')
		copyCorporateRevealProp(vars, to, 'stagger')
		copyCorporateRevealProp(vars, to, 'transformOrigin')

		if (vars.opacity !== undefined) to.autoAlpha = 1
		if (vars.scale !== undefined) to.scale = 1
		if (vars.x !== undefined) to.x = 0
		if (vars.y !== undefined) to.y = 0

		return to
	}

	function copyCorporateRevealProp(source, target, prop) {
		if (source[prop] !== undefined) {
			target[prop] = source[prop]
		}
	}

	function getCorporateRevealConfig(el) {
		const config = {
			shell: {
				opacity: 0,
				y: 16,
				duration: 0.65,
				ease: 'power2.out',
			},
			parts: {
				opacity: 0,
				y: 22,
				scale: 0.99,
				duration: 0.72,
				stagger: { each: 0.055, from: 'start' },
			},
			media: {
				scale: 1.035,
				duration: 1,
				ease: 'power2.out',
				transformOrigin: '50% 55%',
			},
			mediaAt: 0,
		}

		if (el.classList.contains('l-corp-hero__inner')) {
			config.shell = {
				opacity: 0,
				scale: 0.985,
				duration: 0.75,
				ease: 'power2.out',
			}
			config.parts = {
				opacity: 0,
				y: -10,
				scale: 0.985,
				duration: 0.85,
				stagger: 0.06,
				transformOrigin: '50% 50%',
			}
			config.media.scale = 1.04
			config.media.duration = 1.2
		} else if (el.classList.contains('l-corp-intro__inner')) {
			config.parts = {
				opacity: 0,
				x: function (index, node) {
					return node.classList.contains('l-corp-intro__media') ? -16 : 14
				},
				y: function (index, node) {
					return node.classList.contains('l-corp-benefit') ? 8 : 0
				},
				duration: 0.78,
				stagger: { each: 0.055, from: 'start' },
			}
			config.mediaAt = 0.05
		} else if (el.classList.contains('l-corp-products__inner')) {
			config.parts = {
				opacity: 0,
				y: function (index) {
					return index === 0 ? 12 : 18
				},
				scale: function (index) {
					return index === 0 ? 1 : 0.985
				},
				duration: 0.72,
				stagger: { each: 0.045, from: 'start' },
			}
		} else if (el.classList.contains('l-corp-offers__inner')) {
			config.parts = {
				opacity: 0,
				x: function (index, node) {
					return node.classList.contains('l-corp-offers__head') ? -10 : 0
				},
				y: function (index, node) {
					return node.classList.contains('l-corp-offer') ? 18 : 8
				},
				scale: function (index, node) {
					return node.classList.contains('l-corp-offer') ? 0.985 : 1
				},
				duration: 0.74,
				stagger: { each: 0.055, from: 'start' },
			}
		} else if (el.classList.contains('l-corp-contact__inner')) {
			config.parts = {
				opacity: 0,
				x: function (index, node) {
					return node.classList.contains('l-corp-contact__media') ? -14 : 12
				},
				y: function (index, node) {
					return node.classList.contains('l-corp-field') ? 10 : 0
				},
				duration: 0.76,
				stagger: { each: 0.045, from: 'start' },
			}
			config.mediaAt = 0.08
		}

		return config
	}

	function getCorporateRevealParts(el) {
		let selector

		if (el.classList.contains('l-corp-hero__inner')) {
			selector = '.l-corp-hero__title'
		} else if (el.classList.contains('l-corp-intro__inner')) {
			selector =
				'.l-corp-intro__media, .l-corp-intro__heading, .l-corp-intro__body, .l-corp-benefit, .l-corp-intro__cta-wrap'
		} else if (el.classList.contains('l-corp-products__inner')) {
			selector = '.l-corp-products__heading, .l-corp-product-card, .l-corp-carousel__controls'
		} else if (el.classList.contains('l-corp-offers__inner')) {
			selector = '.l-corp-offers__head, .l-corp-offer'
		} else if (el.classList.contains('l-corp-contact__inner')) {
			selector = '.l-corp-contact__media, .l-corp-contact__heading, .l-corp-field, .l-corp-contact__submit'
		}

		return selector ? getVisibleNodes(el.querySelectorAll(selector)) : []
	}

	function getCorporateRevealMedia(el) {
		return getVisibleNodes(
			el.querySelectorAll(
				'.l-corp-hero__img, .l-corp-intro__img, .l-corp-product-card__img, .l-corp-offers__ducks, .l-corp-contact__img'
			)
		)
	}

	function getVisibleNodes(nodes) {
		const seen = []

		return Array.prototype.filter.call(nodes, function (node) {
			if (!node || seen.indexOf(node) !== -1 || !node.getClientRects().length) return false
			seen.push(node)

			return true
		})
	}

	function initCorporatePage(root) {
		initCorporateReveals(root)
	}

	function safeInitCorporatePage(root) {
		try {
			initCorporatePage(root)
		} catch (error) {
			revealCorporateStatic(root)
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			safeInitCorporatePage(document)
		})
	} else {
		safeInitCorporatePage(document)
	}

	if (window.Shopify && window.Shopify.designMode) {
		document.addEventListener('shopify:section:load', function (event) {
			safeInitCorporatePage(event.target)
		})
	}
})()
